import { Capacitor } from '@capacitor/core';
import { Cruise } from '../context/CruiseContext';
import { CapacitorCalendar } from '@ebarooni/capacitor-calendar';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Itinerary } from '../types/itinerary';
import { debug } from '../utils/debug';
import { getItineraryDocId, getLegacyItineraryDocId } from '../utils/itineraryComparator';

export interface CalendarEvent {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location?: string;
  url?: string;
}

export interface CalendarService {
  addToCalendar(event: CalendarEvent): Promise<void>;
}

class WebCalendarService implements CalendarService {
  async addToCalendar(event: CalendarEvent): Promise<void> {
    // Show calendar options modal
    this.showCalendarOptionsModal(event);
  }

  private showCalendarOptionsModal(event: CalendarEvent): void {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
    
    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.className = 'bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto';
    
    modalContent.innerHTML = `
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-800">Add to Calendar</h2>
        <button class="text-gray-400 hover:text-gray-600" data-action="cancel" aria-label="Close">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="p-6 space-y-4">
        <p class="text-gray-600">Choose how you'd like to add this cruise to your calendar:</p>
        
        <div class="space-y-3">
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="google">
            <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Add to Google Calendar
          </button>
          
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="outlook">
            <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
            </svg>
            Add to Outlook Calendar
          </button>
          
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="ics">
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
            Download Calendar File
          </button>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p class="text-sm text-blue-800 flex items-start">
            <svg class="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span><strong>Tip:</strong> The calendar file works with Apple Calendar, Thunderbird, and most other calendar apps.</span>
          </p>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium" data-action="cancel">
            Cancel
          </button>
        </div>
      </div>
    `;
    
    modal.appendChild(modalContent);
    document.body.appendChild(modal);
    
    // Add event listeners
    modalContent.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.closest('[data-action]')?.getAttribute('data-action');
      
      switch (action) {
        case 'google':
          this.addToGoogleCalendar(event);
          break;
        case 'outlook':
          this.addToOutlookCalendar(event);
          break;
        case 'ics':
          this.downloadICSFile(event);
          break;
        case 'cancel':
          break;
      }
      
      // Remove modal
      document.body.removeChild(modal);
    });
    
    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        document.body.removeChild(modal);
      }
    });
  }

  private addToGoogleCalendar(event: CalendarEvent): void {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    // Format dates for Google Calendar URL (YYYYMMDDTHHMMSSZ)
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    
    const startDateFormatted = formatDate(startDate);
    const endDateFormatted = formatDate(endDate);
    
    // Encode parameters for URL
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: event.title,
      dates: `${startDateFormatted}/${endDateFormatted}`,
      details: event.description,
      location: event.location || '',
      url: event.url || ''
    });
    
    const googleCalendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    
    // Open in new tab
    window.open(googleCalendarUrl, '_blank');
  }

  private addToOutlookCalendar(event: CalendarEvent): void {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    // Format dates for Outlook URL (YYYY-MM-DDTHH:MM:SS)
    const formatDate = (date: Date) => {
      return date.toISOString().split('.')[0];
    };
    
    const startDateFormatted = formatDate(startDate);
    const endDateFormatted = formatDate(endDate);
    
    // Encode parameters for URL
    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: event.title,
      startdt: startDateFormatted,
      enddt: endDateFormatted,
      body: event.description,
      location: event.location || ''
    });
    
    const outlookCalendarUrl = `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
    
    // Open in new tab
    window.open(outlookCalendarUrl, '_blank');
  }

  private downloadICSFile(event: CalendarEvent): void {
    // Generate iCalendar (.ics) file content
    const icsContent = this.generateICSContent(event);
    
    // Create and download the .ics file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private generateICSContent(event: CalendarEvent): string {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    // Format dates for iCalendar (UTC format)
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const startDateFormatted = formatDate(startDate);
    const endDateFormatted = formatDate(endDate);
    const now = formatDate(new Date());

    // Escape special characters in description
    const escapeText = (text: string) => {
      return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
    };

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Cruise Tracker//Cruise Event//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@cruisetracker.com`,
      `DTSTAMP:${now}`,
      `DTSTART:${startDateFormatted}`,
      `DTEND:${endDateFormatted}`,
      `SUMMARY:${escapeText(event.title)}`,
      `DESCRIPTION:${escapeText(event.description)}`,
      event.location ? `LOCATION:${escapeText(event.location)}` : '',
      'STATUS:CONFIRMED',
      'TRANSP:OPAQUE',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line !== '').join('\r\n');
  }
}

class NativeCalendarService implements CalendarService {
  async addToCalendar(event: CalendarEvent): Promise<void> {
    const platform = Capacitor.getPlatform();
    debug.log('Platform detected:', platform);
    debug.log('Is native platform:', Capacitor.isNativePlatform());
    
    if (platform === 'ios') {
      // For iOS, show a modal with Apple Calendar, Google Calendar, and ICS download options
      debug.log('Showing iOS calendar modal');
      this.showIOSCalendarOptionsModal(event);
    } else {
      // For Android, show a modal with Google Calendar and Share Elsewhere options
      debug.log('Showing Android calendar modal');
      this.showAndroidCalendarOptionsModal(event);
    }
  }

  private async showIOSCalendarOptionsModal(event: CalendarEvent): Promise<void> {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
    
    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.className = 'bg-white rounded-lg shadow-xl max-w-md w-full p-6';
    
    modalContent.innerHTML = `
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-800">Add to Calendar</h2>
        <button class="text-gray-400 hover:text-gray-600" data-action="cancel" aria-label="Close">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="p-6 space-y-4">
        <p class="text-gray-600">Choose how you'd like to add this cruise to your calendar:</p>
        
        <div class="space-y-3">
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="apple-calendar">
            <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
            </svg>
            Add to Apple Calendar
          </button>
          
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="google-calendar">
            <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Add to Google Calendar
          </button>
          
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="ics">
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
            </svg>
            Share Elsewhere
          </button>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p class="text-sm text-blue-800 flex items-start">
            <svg class="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span><strong>Apple Calendar:</strong> Opens the native Calendar app with event details prefilled. <strong>Google Calendar:</strong> Opens in your browser. <strong>Share Elsewhere:</strong> Shares a calendar file you can import into other apps.</span>
          </p>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium" data-action="cancel">
            Cancel
          </button>
        </div>
      </div>
    `;
    
    modal.appendChild(modalContent);
    document.body.appendChild(modal);
    
    // Handle button clicks
    modalContent.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      const action = target.closest('[data-action]')?.getAttribute('data-action');
      
      if (action === 'apple-calendar') {
        await this.addToAppleCalendar(event);
        this.closeModal(modal);
      } else if (action === 'google-calendar') {
        await this.addToGoogleCalendar(event);
        this.closeModal(modal);
      } else if (action === 'ics') {
        await this.shareElsewhere(event);
        this.closeModal(modal);
      } else if (action === 'cancel') {
        this.closeModal(modal);
      }
    });
    
    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeModal(modal);
      }
    });
  }

  private async showAndroidCalendarOptionsModal(event: CalendarEvent): Promise<void> {
    // Create modal overlay
    const modal = document.createElement('div');
    modal.className = 'fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4';
    
    // Create modal content
    const modalContent = document.createElement('div');
    modalContent.className = 'bg-white rounded-lg shadow-xl max-w-md w-full p-6';
    
    modalContent.innerHTML = `
      <div class="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 class="text-xl font-semibold text-gray-800">Add to Calendar</h2>
        <button class="text-gray-400 hover:text-gray-600" data-action="cancel" aria-label="Close">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
      
      <div class="p-6 space-y-4">
        <p class="text-gray-600">Choose how you'd like to add this cruise to your calendar:</p>
        
        <div class="space-y-3">
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="google-calendar">
            <svg class="w-5 h-5 mr-3" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Add to Google Calendar
          </button>
          
          <button class="w-full flex items-center px-4 py-3 bg-secondary-600 text-white rounded-md hover:bg-secondary-500 transition duration-200 font-medium" data-action="share-elsewhere">
            <svg class="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"/>
            </svg>
            Share Elsewhere
          </button>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p class="text-sm text-blue-800 flex items-start">
            <svg class="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
            <span><strong>Google Calendar:</strong> Opens directly in your calendar app. <strong>Share Elsewhere:</strong> Downloads a calendar file you can share or import into other apps.</span>
          </p>
        </div>
        
        <div class="flex gap-3 pt-4">
          <button class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition duration-200 font-medium" data-action="cancel">
            Cancel
          </button>
        </div>
      </div>
    `;
    
    modal.appendChild(modalContent);
    document.body.appendChild(modal);
    
    // Handle button clicks
    modalContent.addEventListener('click', async (e) => {
      const target = e.target as HTMLElement;
      const action = target.closest('[data-action]')?.getAttribute('data-action');
      
      if (action === 'google-calendar') {
        await this.addToGoogleCalendar(event);
        this.closeModal(modal);
      } else if (action === 'share-elsewhere') {
        await this.shareElsewhere(event);
        this.closeModal(modal);
      } else if (action === 'cancel') {
        this.closeModal(modal);
      }
    });
    
    // Close modal when clicking outside
    modal.addEventListener('click', (e) => {
      if (e.target === modal) {
        this.closeModal(modal);
      }
    });
  }

  private async addToAppleCalendar(event: CalendarEvent): Promise<void> {
    try {
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      
      // Convert dates to milliseconds (Unix timestamp)
      const startDateMs = startDate.getTime();
      const endDateMs = endDate.getTime();
      
      // Call the native Apple Calendar plugin
      const result = await CapacitorCalendar.createEventWithPrompt({
        title: event.title,
        description: event.description,
        location: event.location,
        startDate: startDateMs,
        endDate: endDateMs,
        isAllDay: false
      });
      
      debug.log('Apple Calendar event created:', result);
      
    } catch (error) {
      console.error('Error adding event to Apple Calendar:', error);
      // Fallback to ICS download if Apple Calendar fails
      await this.downloadICSFile(event);
    }
  }

  private async addToGoogleCalendar(event: CalendarEvent): Promise<void> {
    try {
      // Use the exact same logic as the web version
      const startDate = new Date(event.startDate);
      const endDate = new Date(event.endDate);
      
      // Format dates for Google Calendar URL (YYYYMMDDTHHMMSSZ)
      const formatDate = (date: Date) => {
        return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
      };
      
      const startDateFormatted = formatDate(startDate);
      const endDateFormatted = formatDate(endDate);
      
      // Encode parameters for URL (same as web version)
      const params = new URLSearchParams({
        action: 'TEMPLATE',
        text: event.title,
        dates: `${startDateFormatted}/${endDateFormatted}`,
        details: event.description,
        location: event.location || '',
        url: event.url || ''
      });
      
      const googleCalendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
      
      // Use window.open() like the web version - Android will handle this properly
      window.open(googleCalendarUrl, '_blank');
      
    } catch (error) {
      console.error('Error opening Google Calendar:', error);
      // Fallback to ICS sharing if Google Calendar fails
      await this.shareElsewhere(event);
    }
  }

  private async downloadICSFile(event: CalendarEvent): Promise<void> {
    // Generate iCalendar (.ics) file content
    const icsContent = this.generateICSContent(event);
    
    // Create and download the .ics file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  private async shareElsewhere(event: CalendarEvent): Promise<void> {
    // For sharing elsewhere, we'll use the same iCalendar approach
    // but save the file to the device's filesystem first
    const icsContent = this.generateICSContent(event);
    
    // Import required Capacitor plugins
    const { Share } = await import('@capacitor/share');
    const { Filesystem, Directory, Encoding } = await import('@capacitor/filesystem');
    
    try {
      // Generate a unique filename for the calendar event
      const fileName = `cruise-${Date.now()}.ics`;
      
      // Save the ICS file to the Documents directory
      const result = await Filesystem.writeFile({
        path: fileName,
        data: icsContent,
        directory: Directory.Documents,
        encoding: Encoding.UTF8,
      });
      
      // Share the file using the file URI
      await Share.share({
        title: event.title,
        url: result.uri,
        dialogTitle: 'Share Calendar Event'
      });
      
      // Clean up the file after a delay (optional)
      setTimeout(async () => {
        try {
          await Filesystem.deleteFile({
            path: fileName,
            directory: Directory.Documents,
          });
        } catch (error) {
          debug.warn('Could not delete temporary calendar file:', error);
        }
      }, 30000); // 30 seconds delay
      
    } catch (error) {
      console.error('Error saving calendar file:', error);
      throw error;
    }
  }

  private closeModal(modal: HTMLElement): void {
    document.body.removeChild(modal);
  }

  private generateICSContent(event: CalendarEvent): string {
    const startDate = new Date(event.startDate);
    const endDate = new Date(event.endDate);
    
    const formatDate = (date: Date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    const startDateFormatted = formatDate(startDate);
    const endDateFormatted = formatDate(endDate);
    const now = formatDate(new Date());

    const escapeText = (text: string) => {
      return text.replace(/[,;\\]/g, '\\$&').replace(/\n/g, '\\n');
    };

    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Cruise Tracker//Cruise Event//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `UID:${Date.now()}-${Math.random().toString(36).substr(2, 9)}@cruisetracker.com`,
      `DTSTAMP:${now}`,
      `DTSTART:${startDateFormatted}`,
      `DTEND:${endDateFormatted}`,
      `SUMMARY:${escapeText(event.title)}`,
      `DESCRIPTION:${escapeText(event.description)}`,
      event.location ? `LOCATION:${escapeText(event.location)}` : '',
      'STATUS:CONFIRMED',
      'TRANSP:OPAQUE',
      'END:VEVENT',
      'END:VCALENDAR'
    ].filter(line => line !== '').join('\r\n');
  }
}

export class CalendarManager {
  private service: CalendarService;

  constructor() {
    this.service = Capacitor.isNativePlatform() 
      ? new NativeCalendarService() 
      : new WebCalendarService();
  }

  async addCruiseToCalendar(cruise: Cruise): Promise<void> {
    const event = await this.createCalendarEventFromCruise(cruise);
    await this.service.addToCalendar(event);
  }

  /**
   * Parse a time string (e.g., "2:30p", "14:30", "2:30 PM") and return hours and minutes
   * Returns null if parsing fails
   * 
   * Supports formats:
   * - "2:30p" or "2:30pm" -> 14:30
   * - "2:30a" or "2:30am" -> 02:30
   * - "14:30" -> 14:30 (24-hour format)
   * - "2:30 PM" -> 14:30
   */
  private parseTimeString(timeStr: string): { hours: number; minutes: number } | null {
    if (!timeStr || typeof timeStr !== 'string') return null;
    
    const trimmed = timeStr.trim();
    if (!trimmed) return null;
    
    // Handle formats like "2:30p", "2:30pm", "14:30", "2:30 PM"
    const timeRegex = /(\d{1,2}):(\d{2})\s*(a|p|am|pm|A|P|AM|PM)?/i;
    const match = trimmed.match(timeRegex);
    
    if (!match) return null;
    
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3]?.toLowerCase();
    
    // Handle 12-hour format (with a/p/am/pm indicator)
    if (period) {
      if (period === 'p' || period === 'pm') {
        // PM: add 12 hours unless it's 12 PM (noon)
        if (hours !== 12) hours += 12;
      } else if (period === 'a' || period === 'am') {
        // AM: 12 AM becomes 0 (midnight)
        if (hours === 12) hours = 0;
      }
    }
    // If no period specified, assume it's already in 24-hour format
    
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }
    
    return { hours, minutes };
  }

  /**
   * Create a date in local timezone from a YYYY-MM-DD string
   * This avoids timezone issues where "2025-01-17" becomes Jan 16 in some timezones
   */
  private createLocalDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Fetch itinerary for cruise with fallback: try new docId first, then legacy format.
   */
  private async fetchItineraryForCruise(cruise: Cruise): Promise<Itinerary | null> {
    try {
      const newDocId = getItineraryDocId(cruise.cruiseLine, cruise.shipName, cruise.departureDate, cruise.returnDate);
      const newDocRef = doc(db, 'itineraries', newDocId);
      const newDoc = await getDoc(newDocRef);
      if (newDoc.exists()) {
        return newDoc.data() as Itinerary;
      }
      const oldDocId = getLegacyItineraryDocId(cruise.cruiseLine, cruise.shipName, cruise.departureDate);
      const oldDocRef = doc(db, 'itineraries', oldDocId);
      const oldDoc = await getDoc(oldDocRef);
      if (oldDoc.exists()) {
        return oldDoc.data() as Itinerary;
      }
    } catch (error) {
      debug.warn('Error fetching itinerary for cruise:', error);
    }
    return null;
  }

  /**
   * Find departure time from cruise ports or itinerary data
   */
  private async findDepartureTime(cruise: Cruise): Promise<{ hours: number; minutes: number } | null> {
    if (cruise.ports && cruise.ports.length > 0) {
      const departurePort = cruise.ports.find(port => port.date === cruise.departureDate);
      if (departurePort?.departureTime) {
        const parsed = this.parseTimeString(departurePort.departureTime);
        if (parsed) return parsed;
      }
    }
    const itinerary = await this.fetchItineraryForCruise(cruise);
    if (itinerary?.ports && itinerary.ports.length > 0) {
      const departurePort = itinerary.ports.find(port => port.date === cruise.departureDate);
      if (departurePort?.departureTime) {
        const parsed = this.parseTimeString(departurePort.departureTime);
        if (parsed) return parsed;
      }
    }
    return null;
  }

  /**
   * Find arrival time from cruise ports or itinerary data
   */
  private async findArrivalTime(cruise: Cruise): Promise<{ hours: number; minutes: number } | null> {
    if (cruise.ports && cruise.ports.length > 0) {
      const arrivalPort = cruise.ports.find(port => port.date === cruise.returnDate);
      if (arrivalPort?.arrivalTime) {
        const parsed = this.parseTimeString(arrivalPort.arrivalTime);
        if (parsed) return parsed;
      }
    }
    const itinerary = await this.fetchItineraryForCruise(cruise);
    if (itinerary?.ports && itinerary.ports.length > 0) {
      const arrivalPort = itinerary.ports.find(port => port.date === cruise.returnDate);
      if (arrivalPort?.arrivalTime) {
        const parsed = this.parseTimeString(arrivalPort.arrivalTime);
        if (parsed) return parsed;
      }
    }
    return null;
  }

  private async createCalendarEventFromCruise(cruise: Cruise): Promise<CalendarEvent> {
    // Create dates in local timezone to avoid timezone issues
    const startDate = this.createLocalDate(cruise.departureDate);
    const endDate = this.createLocalDate(cruise.returnDate);
    
    // Try to find departure time from ports or itinerary
    const departureTime = await this.findDepartureTime(cruise);
    if (departureTime) {
      startDate.setHours(departureTime.hours, departureTime.minutes, 0, 0);
    } else {
      // Default to 12:00 PM (noon) if not specified
      startDate.setHours(12, 0, 0, 0);
    }
    
    // Try to find arrival time from ports or itinerary
    const arrivalTime = await this.findArrivalTime(cruise);
    if (arrivalTime) {
      endDate.setHours(arrivalTime.hours, arrivalTime.minutes, 0, 0);
    } else {
      // Default to 10:00 AM if not specified
      endDate.setHours(10, 0, 0, 0);
    }

    const title = `${cruise.shipName} - ${cruise.cruiseLine}`;
    
    let description = `Cruise Details:\n\n`;
    description += `Ship: ${cruise.shipName}\n`;
    description += `Cruise Line: ${cruise.cruiseLine}\n`;
    description += `Duration: ${this.calculateDuration(cruise)} days\n`;
    description += `Departure Port: ${cruise.departurePort}\n`;
    
    if (cruise.cabinNumber) {
      description += `Cabin: ${cruise.cabinNumber} (${cruise.cabinType})\n`;
    } else if (cruise.isGuaranteedCabin) {
      description += `Cabin: ${cruise.cabinType} (Guaranteed)\n`;
    } else {
      description += `Cabin: ${cruise.cabinType}\n`;
    }
    
    if (cruise.bookingNumber) {
      description += `Booking Number: ${cruise.bookingNumber}\n`;
    }
    
    if (cruise.diningTime) {
      description += `Dining Time: ${cruise.diningTime}\n`;
    }
    
    if (cruise.travelAgentName) {
      description += `\nTravel Agent:\n`;
      description += `Name: ${cruise.travelAgentName}\n`;
      if (cruise.travelAgentPhone) {
        description += `Phone: ${cruise.travelAgentPhone}\n`;
      }
      if (cruise.travelAgentEmail) {
        description += `Email: ${cruise.travelAgentEmail}\n`;
      }
    }
    
    if (cruise.ports && cruise.ports.length > 0) {
      description += `\nPorts of Call:\n`;
      cruise.ports.forEach((port, index) => {
        description += `${index + 1}. ${port.name || 'Port of Call'}`;
        if (port.date) {
          description += ` (${new Date(port.date).toLocaleDateString()})`;
        }
        if (port.arrivalTime || port.departureTime) {
          description += ` - Arrival: ${port.arrivalTime || 'TBD'}, Departure: ${port.departureTime || 'TBD'}`;
        }
        if (port.excursion) {
          description += `\n   Excursion: ${port.excursion}`;
        }
        description += '\n';
      });
    }
    
    if (cruise.travelDetails && cruise.travelDetails.length > 0) {
      description += `\nTravel Details:\n`;
      cruise.travelDetails.forEach((detail, index) => {
        description += `${index + 1}. ${detail.type.toUpperCase()}: ${detail.company}`;
        if (detail.type === 'airplane' && detail.flightNumber) {
          description += ` (Flight ${detail.flightNumber})`;
        } else if (detail.type === 'train' && detail.trainNumber) {
          description += ` (Train ${detail.trainNumber})`;
        }
        if (detail.reservationNumber) {
          description += `\n   Reservation: ${detail.reservationNumber}`;
        }
        if (detail.departureLocation && detail.arrivalLocation) {
          description += `\n   Route: ${detail.departureLocation} → ${detail.arrivalLocation}`;
        }
        if (detail.departureTime) {
          description += `\n   Departure: ${new Date(detail.departureTime).toLocaleString()}`;
        }
        if (detail.arrivalTime) {
          description += `\n   Arrival: ${new Date(detail.arrivalTime).toLocaleString()}`;
        }
        description += '\n';
      });
    }
    
    if (cruise.notes) {
      description += `\nNotes:\n${cruise.notes}`;
    }
    
    if (cruise.isComplimentaryCasinoSailing) {
      description += `\n\n🎰 This is a complimentary casino sailing!`;
    }

    return {
      title,
      description,
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      location: cruise.departurePort
    };
  }

  private calculateDuration(cruise: Cruise): number {
    const start = new Date(cruise.departureDate);
    const end = new Date(cruise.returnDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }
}

// Export a singleton instance
export const calendarManager = new CalendarManager();
